/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Badge, Button, Card, Col, Collapse, Dropdown, Form, Input, Radio, Row, Select, Space, Tooltip, Typography } from 'antd';
import { CheckOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import BoxIcon from '../Icons/BoxIcon';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Misc imports
import is from '../../utils/is';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useFetchUsersThinListQuery } from '../../store/users/usersApi';
import { ADDITIONAL_ROLES_ORG } from '../../constants/main';
import useDebouncedCallback from '../../utils/hooks/useDebounce';
import { resetInboxFilters, setInboxFilters } from '../../store/liveChat/liveChatThunks';
import { usePrevious } from '../../utils/hooks/usePrevious';
import LiveChatDateFilter from './Filters/LiveChatDateFilter';
import LiveChatAgentFilter from './Filters/LiveChatAgentFilter';
import LiveChatBotsFilter from './Filters/LiveChatBotsFilter';

// Constant declarations
const { Text } = Typography;
const emptyInitialValues = {
  unreadOnly: false,
  orderByUnread: false,
  transferredOnly: false,
  bots: [],
  users: [],
  date: '',
  customDate: [],
  searchBy: 'name',
  isOwner: undefined
};

const LiveChatFilters = ({ isFilteringInbox = false }) => {
  const [searchParams] = useSearchParams();
  const { orgId } = useParams();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const previousTab = usePrevious(searchParams.get('tab'));

  // Store state
  const { currentUser } = useSelector((state) => state.auth);
  const { data: bots } = useFetchBotsQuery({ orgId }, { skip: !orgId });
  const { data: organization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });
  const { data: usersThinList } = useFetchUsersThinListQuery({ orgId, urlParams: { organizationRole: ADDITIONAL_ROLES_ORG.LIVE_AGENT } }, { skip: !orgId });
  const { isFetching: isFetchingConversations } = getCurrentQueryState('fetchConversations', { orgId });
  const { inboxFilters, inboxFilterCount } = useSelector((state) => state.livechat);

  // Local state
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [, updateState] = useState(); // Hook to force a component re-render

  useEffect(() => {
    if (searchParams.get('tab') !== previousTab) {
      dispatch(resetInboxFilters());

      //Clear input fields for all search types
      form.setFieldValue('searchBy', 'name');
      form.setFieldValue('name', '');
      form.setFieldValue('email', '');
      form.setFieldValue('searchText', '');
      form.setFieldValue('sessionId', '');
      form.setFieldValue('conversationId', '');
    }
  }, [searchParams.get('tab')]);

  // This is used to debounce the search input's onChange event
  const debouncedOnChange = useDebouncedCallback(form.submit);

  return (
    <Form
      form={form}
      className="w-100"
      initialValues={
        !is.emptyObject(inboxFilters) ? {
          searchBy: 'name',
          ...inboxFilters
        } : emptyInitialValues
      }
      onFinish={() => {
        dispatch(setInboxFilters({ ...form.getFieldsValue(true), replace: true, skip: 0 }));
      }}
      onFieldsChange={(_, allFields) => {
        updateState(allFields);
      }}
    >
      <Row
        gutter={[8, 8]}
        align="middle"
        className="p-4"
      >
        <Col span={24}>
          <Radio.Group
            value={
              (form.getFieldValue('isOwner') === true || form.getFieldValue('isOwner') === false)
              || (form.getFieldValue('users')?.length === 1 && form.getFieldValue('users').includes(currentUser.id))
                ? 'mine' : 'all'
            }
          >
            <Radio.Button
              value="mine"
              onClick={() => {
                form.setFieldValue('users', [currentUser.id]);
                form.submit();
              }}
              className="px-2"
            >
              {t('Mine')}

              <Dropdown
                menu={{
                  items: [
                    {
                      label: (
                        <Space>
                          {form.getFieldValue('isOwner') === true ? <CheckOutlined className="text-success fs-sm" /> : null}
                          {t('Owner')}
                        </Space>
                      ),
                      key: 'owner',
                      onClick: () => {
                        if (form.getFieldValue('isOwner') === true) {
                          form.setFieldValue('isOwner', undefined);
                        } else {
                          form.setFieldValue('isOwner', true);
                        }
                        form.submit();
                      }
                    },
                    {
                      label: (
                        <Space>
                          {form.getFieldValue('isOwner') === false ? <CheckOutlined className="text-success fs-sm" /> : null}
                          {t('Participant')}
                        </Space>
                      ),
                      key: 'participant',
                      onClick: () => {
                        if (form.getFieldValue('isOwner') === false) {
                          form.setFieldValue('isOwner', undefined);
                        } else {
                          form.setFieldValue('isOwner', false);
                        }
                        form.submit();
                      }
                    }
                  ]
                }}
                trigger={['click']}
              >
                <ArrowDownIcon className="ml-2 bg-layout p-1 v-align-baseline border-radius-base bordered fs-xs" />
              </Dropdown>
            </Radio.Button>

            <Radio.Button
              value="all"
              onChange={() => {
                form.setFieldValue('isOwner', undefined);

                // If the user is the only one selected, clear the selection
                if (form.getFieldValue('users').length === 1 && form.getFieldValue('users').includes(currentUser.id)) {
                  form.setFieldValue('users', []);
                }

                form.submit();
              }}
              className="px-2"
            >
              {t('All')}
            </Radio.Button>
          </Radio.Group>

          {/* Hidden input for isOwner */}
          <Form.Item
            name="isOwner"
            hidden
          >
            <Input />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Row gutter={[8, 8]} wrap={false}>
            <Col flex="auto">
              <Space.Compact className="w-100">
                <Form.Item
                  name={form.getFieldValue('searchBy')}
                  noStyle
                >
                  <Input
                    size="small"
                    placeholder={t('Search inbox')}
                    allowClear
                    onChange={debouncedOnChange}
                    prefix={<SearchOutlined />}
                    style={{
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="searchBy"
                  noStyle
                >
                  <Select
                    size="small"
                    dropdownMatchSelectWidth={false}
                    onChange={() => {
                      // Clear the search input when changing the searchBy field
                      form.setFieldValue('name', undefined);
                      form.setFieldValue('email', undefined);
                      form.setFieldValue('searchText', undefined);
                      form.setFieldValue('sessionId', undefined);
                      form.setFieldValue('conversationId', undefined);
                      form.submit();
                    }}
                    options={[
                      {
                        value: 'name',
                        label: <Text className="fs-sm v-align-top">{t('by username')}</Text>
                      },
                      {
                        value: 'email',
                        label: <Text className="fs-sm v-align-top">{t('by user email')}</Text>
                      },
                      {
                        value: 'searchText',
                        label: <Text className="fs-sm v-align-top">{t('by message')}</Text>
                      },
                      {
                        value: 'sessionId',
                        label: <Text className="fs-sm v-align-top">{t('by Session ID')}</Text>
                      },
                      {
                        value: 'conversationId',
                        label: <Text className="fs-sm v-align-top">{t('by Conversation ID')}</Text>
                      }
                    ]}
                  />
                </Form.Item>
              </Space.Compact>
            </Col>

            <Col flex="none">
              <Dropdown
                placement="bottomRight"
                trigger="click"
                open={filterPanelOpen}
                onOpenChange={open => {
                  if (open) {
                    form.setFieldsValue(inboxFilters);
                  }
                  setFilterPanelOpen(open);
                }}
                forceRender
                overlayClassName="w-100"
                overlayStyle={{ maxWidth: 320 }}
                overlay={
                  <Card
                    id="livechatFilterPanel"
                    className="p-0"
                  >
                    <div className="w-100 p-4">
                      <Row align="middle" justify="space-between" wrap={false}>
                        <Typography.Title level={5}>
                          {t('Filter by')}
                        </Typography.Title>

                        <Button
                          type="link"
                          className="p-0 mb-2 fw-bold"
                          onClick={() => {
                            setFilterPanelOpen(false);
                            form.setFieldsValue(emptyInitialValues);
                            form.submit();
                          }}
                        >
                          {t('Clear all Filters')}
                        </Button>
                      </Row>

                      <Collapse
                        className="filters-dropdown mb-5"
                        accordion
                        expandIconPosition="end"
                        ghost
                        bordered={false}
                      >
                        {
                          bots && !is.emptyArray(bots.items) ? (
                            <Collapse.Panel
                              forceRender
                              className="mb-2"
                              header={
                                <Space size="small">
                                  <FilterOutlined className={form.getFieldValue('bots')?.length ? 'text-primary' : ''}/>
                                  {t('Agent')}
                                </Space>
                              }
                            >
                              <Form.Item
                                name="bots"
                                noStyle
                              >
                                <LiveChatBotsFilter
                                  bots={bots}
                                  form={form}
                                />
                              </Form.Item>
                            </Collapse.Panel>
                          ) : null
                        }

                        {
                          [currentUser, ...(usersThinList ?? []).filter(user => user.id !== currentUser.id)]?.length ? (
                            <Collapse.Panel
                              forceRender
                              className="mb-2"
                              header={
                                <Space size="small">
                                  <FilterOutlined
                                    className={classNames('mr-1', {
                                      'text-primary': form.getFieldValue('users')?.length
                                    })}
                                  />
                                  {t('LiveChat Agent')}
                                </Space>
                              }
                            >
                              <Form.Item
                                name="users"
                                noStyle
                              >
                                <LiveChatAgentFilter
                                  currentUser={currentUser}
                                  usersThinList={usersThinList}
                                  form={form}
                                />
                              </Form.Item>
                            </Collapse.Panel>
                          ) : null
                        }

                        <Collapse.Panel
                          forceRender
                          className="mb-2"
                          header={
                            <Space size="small">
                              <FilterOutlined
                                className={classNames('mr-1', {
                                  'text-primary': form.getFieldValue('date')?.length
                                })}
                              />
                              {t('Date')}
                            </Space>
                          }
                        >
                          <Typography.Text type="secondary" className="fs-sm">
                            {t('Timezone')}: {organization?.timezone}
                          </Typography.Text>

                          <Form.Item
                            name="date"
                            noStyle
                          >
                            <LiveChatDateFilter
                              organization={organization}
                              form={form}
                            />
                          </Form.Item>
                        </Collapse.Panel>
                      </Collapse>

                      <Row align="middle" justify="space-between">
                        <Button
                          onClick={() => {
                            setFilterPanelOpen(false);
                            form.setFieldsValue(inboxFilters);
                          }}
                        >
                          {t('Cancel')}
                        </Button>

                        <Button
                          type="primary"
                          disabled={isFetchingConversations || isFilteringInbox}
                          onClick={() => {
                            setFilterPanelOpen(false);
                            form.submit();
                          }}
                        >
                          {t('Apply Filters')}
                        </Button>
                      </Row>
                    </div>
                  </Card>
                }
              >
                <Badge count={inboxFilterCount}>
                  <Button
                    type="text"
                    shape="circle"
                    icon={<FilterOutlined />}
                    className="bg-layout"
                    onClick={() => {
                      setFilterPanelOpen(!filterPanelOpen);
                      if (filterPanelOpen) {
                        form.setFieldsValue(inboxFilters);
                      }
                    }}
                  />
                </Badge>
              </Dropdown>
            </Col>

            <Col flex="none">
              <Form.Item
                name="orderByUnread"
                noStyle
              >
                <Tooltip
                  title={inboxFilters.orderByUnread ? t('Show unread first') : t('Show default sorting')}
                >
                  <Button
                    type="text"
                    shape="circle"
                    icon={inboxFilters.orderByUnread ? <BoxIcon name="sort-up" /> : <BoxIcon name="sort-down" />}
                    className="bg-layout"
                    onClick={() => {
                      form.setFieldsValue({
                        ...inboxFilters,
                        orderByUnread: !inboxFilters.orderByUnread
                      });
                      form.submit();
                    }}
                  />
                </Tooltip>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

LiveChatFilters.propTypes = {
  filterCount: PropTypes.number,
  isFilteringInbox: PropTypes.bool
};

export default LiveChatFilters;
